import {Component, computed, DestroyRef, inject, signal} from '@angular/core';
import {NavigationEnd, Router, RouterLink, RouterOutlet} from '@angular/router';
import {filter} from 'rxjs';
import {AuthenticationService} from '../../_auth/authentication.service';
import {UserJwt} from '../../models/admin_owner/user/user-jwt';
import {OperatorPanelRoutes} from './operator-panel-routes';
import {SidebarButtonComponent} from '../employee-panel/_components/sidebar-button/sidebar-button.component';
import {ResizeHandleComponent} from '../../_components/resize-handle/resize-handle.component';
import {clampPanelWidth, readStoredPanelWidth} from '../../_components/resize-handle/panel-width';
import {CookieService} from '../../_services/cookie.service';
import {CookieType} from '../../x-models/x-enums/cookie-type';

/** Viewport width, in pixels, at which the rail stops being an overlay drawer — Tailwind's `lg`. */
export const OPERATOR_DESKTOP_BREAKPOINT_PX = 1024;

/**
 * Height of the Operator shell's own top bar, in pixels — the `h-14` on the bar, and the `top-14` every element
 * that hangs beneath it (rail, content region, alert banner) has to agree with.
 *
 * Exported so the pieces placed *outside* this component can be asserted against the real number rather than a
 * copied literal — see `alert-banner.component.spec.ts`, which measures the banner's offset against it (#205:
 * the banner used to inherit the employee panel's 80 px offset and hang 24 px below this bar).
 */
export const OPERATOR_TOP_BAR_HEIGHT_PX = 56;

/**
 * Narrowest the Operator navigation rail may be dragged, in pixels.
 *
 * **Measured, not chosen.** Sweeping the live rail and asking at each width which of its labels still wrap, the
 * last one straightens out at **148 px** (`Bald verfügbar`, the Küche placeholder's sub-label — a longer string
 * than either primary entry). 160 ships that with a small cushion, so a future entry with a slightly longer
 * caption does not immediately start wrapping at the minimum.
 */
export const RAIL_MIN_WIDTH_PX = 160;

/** Widest the rail may be dragged. A rail of labels has no use for more, and the screen it fronts does. */
export const RAIL_MAX_WIDTH_PX = 360;

/** The Tailwind `w-64` the rail shipped with, and still its default. */
export const RAIL_DEFAULT_WIDTH_PX = 256;

const RAIL_WIDTH_STORAGE_KEY = 'operator.railWidth';

/**
 * Minimal shell for the standalone {@link Roles.Operator} baseline role (#205): a slim top bar + a narrow left
 * rail, no admin navbar. Structurally copied from {@link EmployeePanelComponent} — the established pattern for
 * a role-scoped panel with its own chrome — but pared down: Operator carries no calendar/shifts/forms surface,
 * only the live service screens (the open-tables overview today; a kitchen screen later — see the sidebar's
 * placeholder entry, and {@link OperatorPanelRoutes}).
 *
 * Reached only via {@link AuthGuard}'s Operator branch (route-gated to `Roles.Operator`, and the login-landing
 * redirect that keeps a non-operator route from ever rendering for this session).
 *
 * **The rail is drag-resizable on desktop** ({@link ResizeHandleComponent}) and its width is remembered per
 * browser. Below {@link OPERATOR_DESKTOP_BREAKPOINT_PX} the rail is an overlay drawer sized by the `lg:`-gated
 * Tailwind classes, so the custom width — and the handle itself — deliberately do not apply there.
 */
@Component({
  selector: 'app-operator-panel',
  templateUrl: './operator.component.html',
  host: {'[style.--op-rail-width]': 'railWidth() + "px"'},
  styles: [`
    /* Inline component styles are parsed as plain CSS, not SCSS — block comments only in here.

       The rail keeps its Tailwind \`w-64\` / \`lg:left-64\` defaults; these rules only take over at the same
       breakpoint, and win on specificity because view encapsulation adds an attribute selector to each. Below
       \`lg\` the rail is an overlay drawer and the dragged width is irrelevant. */
    @media (min-width: 1024px) {
      .op-rail {
        width: var(--op-rail-width, 16rem);
      }

      .op-content {
        left: var(--op-rail-width, 16rem);
      }
    }
  `],
  imports: [
    RouterLink,
    RouterOutlet,
    SidebarButtonComponent,
    ResizeHandleComponent
  ]
})
export class OperatorComponent {
  private readonly authenticationService = inject(AuthenticationService);
  private readonly router = inject(Router);
  private readonly cookies = inject(CookieService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly OperatorPanelRoutes = OperatorPanelRoutes;
  protected readonly user: UserJwt | null = this.authenticationService.userValue;

  /** Mobile nav open/closed — mirrors {@link EmployeePanelComponent}'s `sidebar` field. */
  protected sidebar = false;

  protected readonly railMinWidth = RAIL_MIN_WIDTH_PX;

  /** Viewport width, tracked so the handle disappears the moment the rail becomes an overlay drawer. */
  private readonly viewportWidth = signal(typeof window === 'undefined' ? 1200 : window.innerWidth);

  /** Whether the rail is a real side rail (and therefore resizable) rather than a mobile drawer. */
  protected readonly desktop = computed(() => this.viewportWidth() >= OPERATOR_DESKTOP_BREAKPOINT_PX);

  /**
   * The width the user asked for. Kept separate from {@link railWidth} so a temporarily narrow window clamps
   * what is *rendered* without destroying the preference.
   */
  protected readonly requestedRailWidth = signal(readStoredPanelWidth(
    RAIL_WIDTH_STORAGE_KEY, RAIL_DEFAULT_WIDTH_PX, RAIL_MIN_WIDTH_PX, RAIL_MAX_WIDTH_PX));

  /**
   * The widest the rail may currently be: its hard ceiling, further capped at a third of the viewport so it can
   * never swallow the screen it is meant to front.
   */
  protected readonly railMaxWidth = computed(() =>
    Math.max(RAIL_MIN_WIDTH_PX, Math.min(RAIL_MAX_WIDTH_PX, Math.round(this.viewportWidth() / 3))));

  /** The width actually applied, clamped into the range the current viewport allows. */
  protected readonly railWidth = computed(() =>
    clampPanelWidth(this.requestedRailWidth(), RAIL_MIN_WIDTH_PX, this.railMaxWidth()));

  constructor() {
    // Close the mobile nav on every navigation, same as EmployeePanelComponent's SidebarComponent.
    this.router.events.pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => this.sidebar = false);

    const onResize = () => this.viewportWidth.set(window.innerWidth);
    window.addEventListener('resize', onResize);
    this.destroyRef.onDestroy(() => window.removeEventListener('resize', onResize));
  }

  protected logout(): void {
    this.authenticationService.logout();
  }

  /**
   * Reopens the cookie consent dialog — `app-cookie-settings`, which {@link AppComponent} renders once at the
   * root of the document and which is therefore already mounted behind this shell.
   *
   * The Operator shell needs an entry point of its own because the app-wide one lives in `navbar-help`, inside
   * the `app-navbar-office` chrome this shell deliberately does not render: without it, a consent decision made
   * once could never be revisited from an Operator session (#205). It sits in the **top bar** rather than the
   * rail so it survives the rail collapsing into a drawer on a phone.
   */
  protected showCookieSettings(): void {
    this.cookies.showSettings();
  }

  /**
   * Remembers the rail width the user settled on — once per drag, not once per pointer move.
   *
   * The write goes through {@link CookieService.setLocalStorage}, the app's only permitted persistence path: a
   * bare `localStorage.setItem` is replaced by a function that throws (the consent guard in
   * `_services/cookie.service.ts`). A refused cookie category, or storage that is unavailable altogether, just
   * means the rail starts at its default next time.
   */
  protected persistRailWidth(width: number): void {
    try {
      this.cookies.setLocalStorage(CookieType.Functional, RAIL_WIDTH_STORAGE_KEY, String(width));
    } catch {
      // Best effort: the preference is simply forgotten on reload.
    }
  }
}
